import { useEffect, useRef, useState } from 'react';
import dayjs from '../../../../config/dayjs';

import { getAvailableData } from '../../../../services/dashboardService';

const defaultDrive = { id: 0, driveName: 'None' };
const defaultCourse = { id: 0, courseName: 'None' };
const defaultBatch = { id: 0, batchName: 'None' };

// Filters kept per saveKey while the app stays loaded, so they survive moving to another page and back.
// In memory on purpose: a page refresh or token expiry (axios interceptor reloads to login) starts fresh.
const savedFiltersByKey = {};

// Runs onChange when value changes. Compared by value, not on mount, so a restored or initial selection is not cleared
// on first load (also safe under React StrictMode, which runs effects twice).
const useOnValueChange = (value, onChange) => {
    const key = JSON.stringify(value);
    const lastKeyRef = useRef(key);
    useEffect(() => {
        if (lastKeyRef.current === key) {
            return;
        }
        lastKeyRef.current = key;
        onChange();
    }, [key]);
}

// [a, b, a, undefined] -> [a, b] (first of each id, missing ones dropped)
const uniqueById = (items) => items.filter((item, index) => item && items.findIndex((other) => other?.id === item.id) === index);

// "1,2" (browser autofill) or [1, 2] -> [1, 2]
const toIdList = (value) => (typeof value === 'string' ? value.split(',').map(Number) : value);

// Dashboard filters (Date, Drive, Course, Batch, Venue), shared by the Dashboard and the Alert Details page.
// initialDate: 'YYYY-MM-DD', '' (all dates) or undefined (today); initialVenueIds: venues selected on first load.
// saveKey: when given, the selection and the last applied body are restored on the next visit (see savedFiltersByKey).
export const useDashboardFilters = ({ initialDate, initialVenueIds = [], saveKey } = {}) => {

    // Read once on open; later renders must not pick up what this visit saves
    const [saved] = useState(() => (saveKey ? savedFiltersByKey[saveKey] : undefined));

    // Drives, courses, batches, exams (projectMasters), exam halls and venues of the project
    const [allData, setAllData] = useState({ drives: [], courses: [], batches: [], projectMasters: [], examHalls: [], venues: [] });

    // Date filter (first filter): limits every other filter to exams on that day; cleared = all dates.
    // Defaults to today so the first load only fetches today's (live) exams.
    const [selectedDate, setSelectedDate] = useState(() => {
        if (saved) {
            return saved.selectedDate;
        }
        if (initialDate === undefined) {
            return dayjs();
        }
        return initialDate ? dayjs(initialDate) : null;
    });
    const [selectedDrive, setSelectedDrive] = useState(saved?.selectedDrive ?? defaultDrive);
    // Course and Venue are multi-select; empty = all
    const [selectedCourseIds, setSelectedCourseIds] = useState(saved?.selectedCourseIds ?? []);
    const [selectedBatch, setSelectedBatch] = useState(saved?.selectedBatch ?? defaultBatch);
    const [selectedVenueIds, setSelectedVenueIds] = useState(saved?.selectedVenueIds ?? initialVenueIds);

    const selectedDateStr = selectedDate && dayjs(selectedDate).isValid() ? dayjs(selectedDate).format('YYYY-MM-DD') : '';
    const isOnSelectedDate = (pm) => !selectedDateStr || String(pm.examDate).slice(0, 10) === selectedDateStr;

    useEffect(() => {
        const fetchAvailableData = async () => {
            try {
                const response = await getAvailableData();
                if (response.code === "OK") {
                    const data = response.data;
                    setAllData({
                        drives: data.drives,
                        courses: data.courses,
                        batches: data.batches,
                        projectMasters: data.projectMasters,
                        examHalls: data.examHalls,
                        venues: data.venues ?? [],
                    });
                }
            } catch (error) {
                console.log('There is an error while getting available data', error);
            }
        }
        fetchAvailableData();
    }, []);

    // Dropdown options, each limited by the filters above it (Date -> Drive -> Course -> Batch -> Venue)
    // No date = every drive; with a date = only drives that have an exam that day
    const driveOptions = [defaultDrive, ...allData.drives.filter((drive) => !selectedDateStr || allData.projectMasters.some((pm) => pm.driveId === drive.id && isOnSelectedDate(pm)))];
    const driveExams = allData.projectMasters.filter((pm) => pm.driveId === selectedDrive.id && isOnSelectedDate(pm));
    const courses = [defaultCourse, ...uniqueById(driveExams.map((pm) => allData.courses.find((course) => course.id === pm.courseId)))];
    const courseExams = driveExams.filter((pm) => selectedCourseIds.includes(pm.courseId));
    const batches = [defaultBatch, ...uniqueById(courseExams.map((pm) => allData.batches.find((batch) => batch.id === pm.batchId)))];
    // Venues of the exams matching the filters ("None" = any for that level)
    const matchingExamIds = allData.projectMasters
        .filter((pm) => (!selectedDrive.id || pm.driveId === selectedDrive.id)
            && (!selectedCourseIds.length || selectedCourseIds.includes(pm.courseId))
            && (!selectedBatch.id || pm.batchId === selectedBatch.id)
            && isOnSelectedDate(pm))
        .map((pm) => pm.id);
    const venueOptions = allData.venues.filter((venue) => allData.examHalls.some((eh) => eh.venueMasterId === venue.id && matchingExamIds.includes(eh.projectMasterId)));

    // A filter changing clears the ones below it, since their options change
    useOnValueChange([selectedDrive.id, selectedDateStr], () => {
        setSelectedCourseIds([]);
        setSelectedBatch(defaultBatch);
    });
    useOnValueChange([selectedCourseIds, selectedDateStr], () => {
        setSelectedBatch(defaultBatch);
    });
    useOnValueChange([selectedDrive.id, selectedCourseIds, selectedBatch.id, selectedDateStr], () => {
        setSelectedVenueIds([]);
    });

    const save = (changes) => {
        if (saveKey) {
            savedFiltersByKey[saveKey] = { ...savedFiltersByKey[saveKey], ...changes };
        }
    }
    useEffect(() => {
        save({ selectedDate, selectedDrive, selectedCourseIds, selectedBatch, selectedVenueIds });
    }, [saveKey, selectedDate, selectedDrive, selectedCourseIds, selectedBatch, selectedVenueIds]);

    const handleDateChange = (date) => {
        setSelectedDate(date);
        setSelectedDrive(defaultDrive);
    }
    const handleDriveChange = (driveId) => {
        setSelectedDrive(driveOptions.find((drive) => drive.id === driveId));
    }
    const handleCourseChange = (value) => {
        setSelectedCourseIds(toIdList(value));
    }
    const handleBatchChange = (batchId) => {
        setSelectedBatch(batches.find((batch) => batch.id === batchId));
    }
    const handleVenueChange = (value) => {
        setSelectedVenueIds(toIdList(value));
    }

    // Request body of center-summary / alert-summary for the current dropdown values.
    // Exam and exam hall have no dropdown on the dashboard, so they are always 0 (= all).
    const getFilterBody = () => ({
        driveId: selectedDrive.id,
        courseId: selectedCourseIds.length === 1 ? selectedCourseIds[0] : 0,
        courseIds: selectedCourseIds,
        examDate: selectedDateStr,
        batchId: selectedBatch.id,
        projectMasterId: 0,
        examHallId: 0,
        venueMasterIds: selectedVenueIds,
    });

    return {
        allData,
        selectedDate,
        selectedDateStr,
        selectedDrive,
        driveOptions,
        courses,
        selectedCourseIds,
        batches,
        selectedBatch,
        venueOptions,
        selectedVenueIds,
        handleDateChange,
        handleDriveChange,
        handleCourseChange,
        handleBatchChange,
        handleVenueChange,
        getFilterBody,
        // Last applied ("Go") body from the previous visit; the page reloads its data with it
        savedAppliedBody: saved?.appliedBody ?? null,
        saveAppliedBody: (body) => save({ appliedBody: body }),
    };
}
